import type { ReportOptions } from '../../shared/types';
import type { IncludeFilterOption, ReportLogoAction } from './types';
import { post, put } from '../../../../../redux/api';
import { window } from '../../../../../globals';
import type {
  UpdateOrganizationDefaultReportCustomTextRequest,
  UpdateOrganizationDefaultReportCustomTextResponse,
} from '../../../../../../typings/api';

/**
 * Take the 'includes' choices from a V2 Report Generation preview and make their canonical URL params
 */
export function buildIncludeParams(includeOptions: IncludeFilterOption[]): ReportOptions {
  return Object.fromEntries(includeOptions.map((option) => [option, true]));
}

/**
 * Update the report settings (upload/remove log and set custom text)
 */
export async function updateReportSettings({
  shouldSetDefault,
  file,
  reportLogoAction,
  shouldPropagate,
  customText,
  updateReportCustomTextEndpoint,
}: {
  shouldSetDefault: boolean;
  file: File | null;
  reportLogoAction: ReportLogoAction | null;
  customText: string;
  shouldPropagate: boolean;
  updateReportCustomTextEndpoint: string;
}): Promise<void> {
  const organizationId = window.user?.organization?.id;
  if (reportLogoAction === 'SAVE' && file) {
    const { data } = await post<{ filename: string; contentType: string }, { signedUrl: string }>(
      '/api/organization/logo/get_signed_url',
      { filename: file.name, contentType: file.type },
    );

    if (!data) {
      throw new Error('Failed to get signed URL');
    }

    await fetch(data.signedUrl, {
      method: 'PUT',
      body: file,
    });

    await post('/api/organization/logo', { filename: file.name });
    if (window.user && window.user.organization) {
      window.user.organization.image = file.name;
    }
  }

  if (reportLogoAction === 'REMOVE') {
    await post('/api/organization/logo', { unset: true });
    if (window.user && window.user.organization) {
      window.user.organization.image = undefined;
    }
  }

  if (shouldSetDefault) {
    await put<UpdateOrganizationDefaultReportCustomTextRequest, UpdateOrganizationDefaultReportCustomTextResponse>(
      `/api/organizations/${organizationId}/defaultReportCustomText`,
      {
        shouldPropagate,
        reportCustomText: customText,
      },
    );
  }
  await put(updateReportCustomTextEndpoint, { reportCustomText: customText });
}
